import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Button,
  Stack,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch,
  Link,
  Tooltip,
} from "@mui/material";
import RuleIcon from "@mui/icons-material/Rule";
import ReplayIcon from "@mui/icons-material/Replay";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import DoneIcon from "@mui/icons-material/Done";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import FlagIcon from "@mui/icons-material/Flag";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import dayjs from "dayjs";
import { useTheme, alpha } from "@mui/material/styles";
import {
  accentForIndex,
  adminSurfaceSx,
  adminPageSx,
  adminChipSx,
  MONO,
} from "./adminStyles";
import AdminPageHeader from "./AdminPageHeader";
import AdminSectionHeader from "./AdminSectionHeader";
import ChatTimelineDialog from "./ChatTimelineDialog";
import WorkoutForensicsDialog from "./WorkoutForensicsDialog";
import { adminRequest } from "../../utils/adminApi";

// Coach-feedback triage console (doc 08). Reads the K3.3 triage aggregate from
// the worker via the functions/api/admin/getFeedbackTriage proxy → worker
// GET /api/admin/feedback-triage. Renders the ranked 👎/correction clusters and
// the DRAFT candidate golden-case stubs (copyable) so curating the coach eval
// set is a click in the console rather than a CLI session. The worker enforces
// admin authorization server-side; this page just needs an admin token.
//
// The candidate stubs are DRAFTS for human review — copy → paste toward
// evals/coach/cases/ behind the human gate; nothing here writes the eval set.
// A single coach reply can attract more than one feedback event — e.g. a 👎
// rating AND a text correction — which arrive as separate events that share one
// message_id. The triaged-state is keyed by message_id (marking one clears them
// all), so rendering an item per event produced confusing duplicate rows for the
// same reply. Merge by message_id into ONE row that carries every signal (down +
// correction), keeping the earliest timestamp and any triage_state.
export function mergeItemsByMessageId(items = []) {
  const byId = new Map();
  for (const it of items) {
    const prev = byId.get(it.message_id);
    if (!prev) {
      byId.set(it.message_id, { ...it });
      continue;
    }
    byId.set(it.message_id, {
      ...prev,
      rating:
        prev.rating === "down" || it.rating === "down"
          ? "down"
          : prev.rating ?? it.rating ?? null,
      correction_text: prev.correction_text ?? it.correction_text ?? null,
      prompt: prev.prompt ?? it.prompt ?? null,
      user_email: prev.user_email ?? it.user_email ?? null,
      triage_state: prev.triage_state ?? it.triage_state ?? null,
      created_at:
        typeof prev.created_at === "number" &&
        typeof it.created_at === "number"
          ? Math.min(prev.created_at, it.created_at)
          : prev.created_at ?? it.created_at ?? null,
    });
  }
  return [...byId.values()];
}

export default function FeedbackTriage() {
  const theme = useTheme();
  // env-aware: prod uses the authSlice token as today; staging uses the held
  // staging token and appends the routing-only ?env=staging (adminRequest).
  const { env, token } = useSelector(adminRequest);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState("");
  // Selected feedback item whose chat timeline is open (hybra #422). null = closed.
  const [timelineItem, setTimelineItem] = useState(null);
  // Selected workout flag whose forensics dialog is open (worker #444). null = closed.
  const [forensicsFlag, setForensicsFlag] = useState(null);
  // doc 55 Delta 1: default view hides handled items (worker filters state=open).
  // "Show handled" flips to state=all so dispositions + issue links are visible.
  const [showHandled, setShowHandled] = useState(false);

  const runTriage = useCallback(async () => {
    // Skip in staging mode until a staging token is held — the AdminLayout login
    // dialog handles auth.
    if (env === "staging" && !token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const state = showHandled ? "all" : "open";
      // env is routing-only; appended after the worker's own ?state= param and
      // stripped by the proxy before forwarding.
      const envSuffix = env === "staging" ? "&env=staging" : "";
      const result = await axios.get(
        `/api/admin/getFeedbackTriage?state=${state}${envSuffix}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setData(result.data);
    } catch (err) {
      console.error("Failed to run feedback triage:", err);
      setError("Failed to run feedback triage.");
    } finally {
      setLoading(false);
    }
  }, [env, token, showHandled]);

  useEffect(() => {
    runTriage();
  }, [runTriage]);

  // doc 55 Delta 1: mark a feedback item triaged/dismissed. POSTs through the
  // updateFeedbackTriageState proxy (messageId as query param); the worker
  // derives `actor` from the token. On success we refetch so the item drops out
  // of the open queue (or picks up its new disposition badge in the all view).
  // `key` is the triage-state key: message_id for coach-reply items, flag id
  // for workout flags (hybra #529) — same endpoint, same state store.
  const markState = useCallback(
    async (key, status) => {
      try {
        const envSuffix = env === "staging" ? "&env=staging" : "";
        await axios.post(
          `/api/admin/updateFeedbackTriageState?messageId=${encodeURIComponent(
            key
          )}${envSuffix}`,
          { status },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSnack(status === "triaged" ? "Marked triaged" : "Dismissed");
        runTriage();
      } catch (err) {
        console.error("Failed to update feedback triage state:", err);
        setSnack("Update failed — try again.");
      }
    },
    [env, token, runTriage]
  );

  // Clusters worst-first: most 👎/corrections at the top.
  const clusters = useMemo(() => {
    const list = data?.clusters ?? [];
    return [...list].sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
  }, [data]);

  const candidates = useMemo(() => data?.candidates ?? [], [data]);
  const negativesCount = data?.negatives_count ?? 0;
  const positivesCount = data?.positives_count ?? 0;
  // Workout flags (hybra #529): 👍/👎 + suggestion/issue captured on a workout
  // in the iOS app, surfaced by the worker in this same aggregate. Designed
  // optionality: an older worker deploy omits the field entirely — treat as
  // empty so the console stays functional either way.
  const workoutFlags = useMemo(() => data?.workout_flags ?? [], [data]);
  const workoutFlagsCount = data?.workout_flags_count ?? 0;

  const copyText = useCallback(async (text, message) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnack(message);
    } catch (err) {
      console.error("Clipboard write failed:", err);
      setSnack("Copy failed — clipboard unavailable.");
    }
  }, []);

  const copyCandidate = useCallback(
    (candidate) =>
      copyText(JSON.stringify(candidate, null, 2), "Candidate JSON copied"),
    [copyText]
  );

  const copyAllJsonl = useCallback(() => {
    const jsonl = candidates.map((c) => JSON.stringify(c)).join("\n");
    copyText(jsonl, `Copied ${candidates.length} candidates as JSONL`);
  }, [candidates, copyText]);

  return (
    <Box>
      <Box sx={adminPageSx}>
        <AdminPageHeader
          icon={<RuleIcon />}
          title="Coach feedback triage"
          subtitle={
            <>
              Ranked 👎 / correction clusters + draft golden-case stubs (doc 08).
              Drafts for human review — copy toward{" "}
              <code>evals/coach/cases/</code>.
            </>
          }
          actions={
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={showHandled}
                    onChange={(e) => setShowHandled(e.target.checked)}
                    disabled={loading}
                    slotProps={{ input: { "aria-label": "Show handled" } }}
                  />
                }
                label="Show handled"
              />
              <Button
                variant="contained"
                startIcon={<ReplayIcon />}
                onClick={runTriage}
                disabled={loading}
              >
                Run triage
              </Button>
            </>
          }
        />

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Header summary */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card sx={adminSurfaceSx(theme)}>
                  <CardContent>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <ThumbDownAltIcon
                        sx={{ color: theme.palette.error.main }}
                      />
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.error.main,
                        }}
                      >
                        {negativesCount.toLocaleString()}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      👎 / corrections captured
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card sx={adminSurfaceSx(theme)}>
                  <CardContent>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <ThumbUpAltIcon
                        sx={{ color: theme.palette.success.main }}
                      />
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.success.main,
                        }}
                      >
                        {positivesCount.toLocaleString()}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      👍 positives
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card sx={adminSurfaceSx(theme)}>
                  <CardContent>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <FlagIcon sx={{ color: theme.palette.warning.main }} />
                      <Typography
                        variant="h4"
                        data-testid="workout-flags-count"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.warning.main,
                        }}
                      >
                        {workoutFlagsCount.toLocaleString()}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      workout flags
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card sx={adminSurfaceSx(theme)}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {data?.generated_at
                        ? dayjs(data.generated_at).format("MMM D, YYYY HH:mm")
                        : "—"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Triage generated
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {negativesCount === 0 ? (
              <Alert
                severity="success"
                variant="outlined"
                data-testid="feedback-triage-empty"
                sx={{
                  borderColor: alpha(theme.palette.success.main, 0.5),
                  "& .MuiAlert-message": {
                    color: theme.palette.text.primary,
                  },
                }}
              >
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                  No 👎 / corrections captured yet
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Nothing to triage — once the coach starts collecting thumbs-down
                  and corrections, the worst-first clusters and draft golden-case
                  stubs will appear here.
                </Typography>
              </Alert>
            ) : (
              <>
                {/* Clusters, worst-first */}
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, mb: 1 }}
                >
                  Clusters (worst first)
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2} sx={{ mb: 4 }}>
                  {clusters.map((cluster, i) => {
                    const accent = accentForIndex(i);
                    return (
                      <Card
                        key={`${cluster.reply_excerpt}-${i}`}
                        sx={adminSurfaceSx(theme, { accent })}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "space-between",
                              gap: 2,
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                              }}
                            >
                              {cluster.reply_excerpt || "(no reply excerpt)"}
                            </Typography>
                            <Chip
                              label={`${cluster.count ?? 0}×`}
                              size="small"
                              sx={{
                                ...adminChipSx(theme, accent),
                                fontWeight: 700,
                                flexShrink: 0,
                              }}
                            />
                          </Box>
                          <Divider sx={{ mb: 1.5 }} />
                          <Stack spacing={1.5}>
                            {mergeItemsByMessageId(cluster.items).map((item, j) => {
                              // Clickable → opens the chat timeline dialog. The
                              // worker adds item.user_email additively; until it
                              // deploys the field may be absent, so keep the item
                              // non-clickable (no broken timeline request) with a
                              // subtle "user unavailable" hint.
                              const clickable = !!item.user_email;
                              return (
                              <Box
                                key={`${item.message_id}-${j}`}
                                role={clickable ? "button" : undefined}
                                tabIndex={clickable ? 0 : undefined}
                                data-testid="feedback-triage-item"
                                onClick={
                                  clickable
                                    ? () => setTimelineItem(item)
                                    : undefined
                                }
                                onKeyDown={
                                  clickable
                                    ? (e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                          e.preventDefault();
                                          setTimelineItem(item);
                                        }
                                      }
                                    : undefined
                                }
                                sx={{
                                  borderLeft: `3px solid ${alpha(accent, 0.5)}`,
                                  pl: 1.5,
                                  borderRadius: 1,
                                  cursor: clickable ? "pointer" : "default",
                                  transition: "background-color .16s ease",
                                  ...(clickable && {
                                    "&:hover": {
                                      backgroundColor: alpha(accent, 0.08),
                                    },
                                  }),
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 0.5,
                                  }}
                                >
                                  {/* One reply can attract both a 👎 and a
                                      correction (merged into this row) — show a
                                      chip per signal rather than one-or-the-other. */}
                                  {item.rating === "down" && (
                                    <Chip
                                      size="small"
                                      icon={<ThumbDownAltIcon />}
                                      label="👎 down"
                                      color="error"
                                      variant="outlined"
                                    />
                                  )}
                                  {item.correction_text && (
                                    <Chip
                                      size="small"
                                      label="correction"
                                      color="warning"
                                      variant="outlined"
                                    />
                                  )}
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    {item.created_at
                                      ? dayjs(item.created_at).format(
                                          "MMM D, HH:mm"
                                        )
                                      : ""}
                                  </Typography>
                                </Box>
                                {item.prompt && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: theme.palette.text.primary,
                                      mb: 0.5,
                                    }}
                                  >
                                    <strong>Prompt:</strong> {item.prompt}
                                  </Typography>
                                )}
                                {item.correction_text && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: theme.palette.warning.main,
                                      mb: 0.5,
                                    }}
                                  >
                                    <strong>Correction:</strong>{" "}
                                    {item.correction_text}
                                  </Typography>
                                )}
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: theme.palette.text.secondary,
                                    fontFamily: MONO,
                                  }}
                                >
                                  conv {item.conversation_id} · msg{" "}
                                  {item.message_id}
                                  {clickable ? (
                                    " · view timeline"
                                  ) : (
                                    <Box
                                      component="span"
                                      sx={{
                                        fontStyle: "italic",
                                        color: theme.palette.text.secondary,
                                      }}
                                    >
                                      {" "}
                                      · user unavailable
                                    </Box>
                                  )}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    mt: 1,
                                  }}
                                >
                                  {item.triage_state ? (
                                    <Chip
                                      size="small"
                                      data-testid="feedback-triage-state-badge"
                                      label={
                                        item.triage_state.issue_url ? (
                                          <Link
                                            href={item.triage_state.issue_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            sx={{
                                              color: "inherit",
                                              textDecoration: "underline",
                                            }}
                                          >
                                            {item.triage_state.status}
                                            {item.triage_state.case_id
                                              ? ` · ${item.triage_state.case_id}`
                                              : ""}
                                          </Link>
                                        ) : (
                                          `${item.triage_state.status}${
                                            item.triage_state.case_id
                                              ? ` · ${item.triage_state.case_id}`
                                              : ""
                                          }`
                                        )
                                      }
                                      color={
                                        item.triage_state.status === "dismissed"
                                          ? "default"
                                          : "success"
                                      }
                                      variant="outlined"
                                    />
                                  ) : (
                                    <>
                                      <Tooltip title="Mark triaged">
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="success"
                                          startIcon={<DoneIcon />}
                                          data-testid="feedback-triage-mark-triaged"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            markState(item.message_id, "triaged");
                                          }}
                                        >
                                          Mark triaged
                                        </Button>
                                      </Tooltip>
                                      <Tooltip title="Dismiss">
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="inherit"
                                          startIcon={<NotInterestedIcon />}
                                          data-testid="feedback-triage-dismiss"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            markState(item.message_id, "dismissed");
                                          }}
                                        >
                                          Dismiss
                                        </Button>
                                      </Tooltip>
                                    </>
                                  )}
                                </Box>
                              </Box>
                              );
                            })}
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>

                {/* Candidate golden-case stubs */}
                <AdminSectionHeader
                  label={`Candidate golden-case stubs (${candidates.length})`}
                  accent={theme.palette.tertiary?.main}
                  divider={false}
                  sx={{ mb: 1 }}
                  action={
                    candidates.length > 0 ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={copyAllJsonl}
                      >
                        Copy all as JSONL
                      </Button>
                    ) : undefined
                  }
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 2,
                  }}
                >
                  DRAFTS for human review — paste into{" "}
                  <code>evals/coach/cases/</code> only after a human checks the
                  ideal answer + rubric.
                </Typography>
                <Stack spacing={2}>
                  {candidates.map((candidate) => (
                    <Card
                      key={candidate.id}
                      sx={adminSurfaceSx(theme)}
                      data-testid="feedback-triage-candidate"
                    >
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily:
                                  "ui-monospace, SFMono-Regular, Menlo, monospace",
                                color: theme.palette.text.secondary,
                              }}
                            >
                              {candidate.id}
                            </Typography>
                            <Chip
                              size="small"
                              label={candidate.category}
                              sx={adminChipSx(theme)}
                            />
                          </Box>
                          <Button
                            size="small"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => copyCandidate(candidate)}
                          >
                            Copy
                          </Button>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ color: theme.palette.text.primary, mb: 0.5 }}
                        >
                          <strong>Prompt:</strong> {candidate.prompt}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: theme.palette.text.primary, mb: 0.5 }}
                        >
                          <strong>Ideal:</strong> {candidate.ideal}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          <strong>Rubric:</strong> {candidate.rubric}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                  {candidates.length === 0 && (
                    <Typography
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      No candidate stubs generated for this triage run.
                    </Typography>
                  )}
                </Stack>
              </>
            )}

            {/* Workout flags (hybra #529): 👍/👎 + suggestion/issue captured on
                a workout in the iOS app. Rendered OUTSIDE the coach-feedback
                empty-state branch — a workout can be flagged even when no coach
                👎s exist. Triage actions reuse the same triage-state store,
                keyed by the flag's id instead of a message_id. */}
            <Box sx={{ mt: 4 }} data-testid="workout-flags-section">
              <AdminSectionHeader
                label={`Workout flags (${workoutFlags.length})`}
                accent={theme.palette.warning.main}
                sx={{ mb: 2 }}
              />
              {workoutFlags.length === 0 ? (
                <Typography
                  data-testid="workout-flags-empty"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  No workout flags — flags captured on a workout in the iOS app
                  (👍/👎 + suggestion/issue) will appear here.
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {workoutFlags.map((flag) => (
                    <Card
                      key={flag.id}
                      sx={adminSurfaceSx(theme)}
                      data-testid="workout-flag-item"
                    >
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          {flag.rating === "down" ? (
                            <Chip
                              size="small"
                              icon={<ThumbDownAltIcon />}
                              label="👎 down"
                              color="error"
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              size="small"
                              icon={<ThumbUpAltIcon />}
                              label="👍 up"
                              color="success"
                              variant="outlined"
                            />
                          )}
                          {flag.category && (
                            <Chip
                              size="small"
                              label={flag.category}
                              color={
                                flag.category === "issue"
                                  ? "warning"
                                  : "default"
                              }
                              variant="outlined"
                            />
                          )}
                          <Typography
                            variant="caption"
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            {flag.created_at
                              ? dayjs(flag.created_at).format(
                                  "MMM D, YYYY HH:mm"
                                )
                              : ""}
                          </Typography>
                        </Box>
                        {flag.text && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.primary,
                              mb: 0.5,
                            }}
                          >
                            {flag.text}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontFamily: MONO,
                          }}
                        >
                          {flag.workout_name ?? flag.workout_id}
                          {flag.user_email ? ` · ${flag.user_email}` : ""}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          {/* Workout-forensics drill-down (worker #444): the
                              full story of the flagged workout — draft/chat
                              provenance, target-vs-actual, rest events. Needs
                              the owner email to query; older flags without
                              user_email keep the button disabled. */}
                          <Tooltip title="Workout forensics">
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<TroubleshootIcon />}
                                data-testid="workout-flag-forensics"
                                disabled={!flag.user_email}
                                onClick={() => setForensicsFlag(flag)}
                              >
                                Forensics
                              </Button>
                            </span>
                          </Tooltip>
                          {flag.triage_state ? (
                            <Chip
                              size="small"
                              data-testid="workout-flag-state-badge"
                              label={
                                flag.triage_state.issue_url ? (
                                  <Link
                                    href={flag.triage_state.issue_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      color: "inherit",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    {flag.triage_state.status}
                                    {flag.triage_state.case_id
                                      ? ` · ${flag.triage_state.case_id}`
                                      : ""}
                                  </Link>
                                ) : (
                                  `${flag.triage_state.status}${
                                    flag.triage_state.case_id
                                      ? ` · ${flag.triage_state.case_id}`
                                      : ""
                                  }`
                                )
                              }
                              color={
                                flag.triage_state.status === "dismissed"
                                  ? "default"
                                  : "success"
                              }
                              variant="outlined"
                            />
                          ) : (
                            <>
                              <Tooltip title="Mark triaged">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  startIcon={<DoneIcon />}
                                  data-testid="workout-flag-mark-triaged"
                                  onClick={() =>
                                    markState(flag.id, "triaged")
                                  }
                                >
                                  Mark triaged
                                </Button>
                              </Tooltip>
                              <Tooltip title="Dismiss">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="inherit"
                                  startIcon={<NotInterestedIcon />}
                                  data-testid="workout-flag-dismiss"
                                  onClick={() =>
                                    markState(flag.id, "dismissed")
                                  }
                                >
                                  Dismiss
                                </Button>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          </>
        )}
      </Box>
      <Snackbar
        open={!!snack}
        autoHideDuration={2500}
        onClose={() => setSnack("")}
        message={snack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
      <ChatTimelineDialog
        open={!!timelineItem}
        onClose={() => setTimelineItem(null)}
        conversationId={timelineItem?.conversation_id}
        userEmail={timelineItem?.user_email}
        messageId={timelineItem?.message_id}
      />
      <WorkoutForensicsDialog
        open={!!forensicsFlag}
        onClose={() => setForensicsFlag(null)}
        workoutId={forensicsFlag?.workout_id}
        userEmail={forensicsFlag?.user_email}
      />
    </Box>
  );
}
